// api.js - Centralized API utility with device ID header support
import API_URL from './config';
import getDeviceId from './utils/device';

/**
 * Makes an authenticated API request with device ID header.
 * @param {string} endpoint - API endpoint (e.g., '/api/toggle-clock')
 * @param {object} options - Fetch options (method, body, etc.)
 * @param {string} token - Authentication token
 * @param {string} authType - Type of auth header: 'bearer' (default) or 'employee-token'
 * @returns {Promise<Response>} - Fetch response
 */
export const apiRequest = async (endpoint, options = {}, token = null, authType = 'bearer') => {
  const deviceId = getDeviceId();
  
  const headers = {
    'Content-Type': 'application/json',
    'X-Device-ID': deviceId,
    ...options.headers,
  };
  
  // Add authorization header if token provided
  if (token) {
    if (authType === 'employee-token') {
      headers['Employee-Token'] = token;
    } else {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }
  
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  });
  
  return response;
};

/**
 * Makes an authenticated GET request.
 */
export const apiGet = async (endpoint, token, authType = 'bearer') => {
  return apiRequest(endpoint, { method: 'GET' }, token, authType);
};

/**
 * Makes an authenticated POST request.
 */
export const apiPost = async (endpoint, body, token, authType = 'bearer') => {
  return apiRequest(endpoint, {
    method: 'POST',
    body: JSON.stringify(body),
  }, token, authType);
};

/**
 * Makes an authenticated PUT request.
 */
export const apiPut = async (endpoint, body, token, authType = 'bearer') => {
  return apiRequest(endpoint, {
    method: 'PUT',
    body: JSON.stringify(body),
  }, token, authType);
};

/**
 * Makes an authenticated DELETE request.
 */
export const apiDelete = async (endpoint, token, authType = 'bearer') => {
  return apiRequest(endpoint, { method: 'DELETE' }, token, authType);
};

export default apiRequest;
